import React, { FormEvent, PropsWithChildren, useState } from "react";
import ErrorMessage from "./ErrorMessage";
import { Spacing } from "../Containers";

export type FieldValidationsType = ReturnType<typeof createFieldValidations>;

export function createFieldValidations<
  T extends Record<string, any>,
  Y extends Record<
    string,
    {
      deps: Array<keyof Y>;
      validation: () => string | undefined;
    }
  >
>(data = {} as T, userValidations = {} as Y) {
  const [isEverythingValid, setEverythingIsValid] = useState(false);
  const [internalShouldValidateField, setShouldValidateField] = useState(
    Object.keys(data).reduce((acc, key) => {
      acc[key] = false;
      return acc;
    }, {} as Record<string, boolean>) as Record<keyof Y, boolean>
  );
  const [internalValidations, setValidations] = useState(
    Object.keys(userValidations).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {} as Record<string, boolean>)
  );
  const shouldValidateField = internalShouldValidateField as Record<keyof Y, boolean>;
  const validations = internalValidations as Record<keyof Y, boolean>;
  const [errorMessages, setErrorMessages] = useState({} as Partial<Record<keyof Y, string>>);

  function clearValidation(field?: keyof Y | "all") {
    const newValidations = {
      ...validations,
    };
    const newShouldValidateField = {
      ...shouldValidateField,
    };
    if (field === "all") {
      Object.keys(userValidations).forEach((key: keyof Y) => {
        newShouldValidateField[key] = false;
        newValidations[key] = true;
      });
    } else if (field) {
      newShouldValidateField[field] = false;
      newValidations[field] = true;
    }
    setErrorMessages({});
    setValidations(newValidations);
    setShouldValidateField(newShouldValidateField);
    setEverythingIsValid(true);
  }

  function validate(field?: keyof Y | "all", makeUpdates = true) {
    const newValidations = {
      ...validations,
    };
    const newErrorMessages: Partial<Record<keyof Y, string>> = {};
    const newShouldValidateField = {
      ...shouldValidateField,
    };
    if (field === "all") {
      Object.keys(userValidations).forEach((key: keyof Y) => {
        newShouldValidateField[key] = true as any;
      });
    } else if (field) {
      newShouldValidateField[field] = true as any;
    }
    let allValidationsPassed = true;
    Object.keys(userValidations).forEach((fieldName: keyof Y) => {
      let validationResult;
      if (newShouldValidateField[fieldName]) {
        validationResult = userValidations[fieldName].validation();
      }
      const isValid = validationResult === undefined;
      if (validationResult) {
        newErrorMessages[fieldName] = validationResult;
      }
      if (!isValid) allValidationsPassed = false;
      if (userValidations[fieldName].deps.length === 0) {
        newValidations[fieldName] = isValid as any;
      } else {
        const allDepsShouldValidate = userValidations[fieldName].deps.every((dep) => newShouldValidateField[dep]);
        if (allDepsShouldValidate) {
          newValidations[fieldName] = isValid as any;
        }
      }
    });

    if (makeUpdates) {
      setErrorMessages(newErrorMessages);
      setValidations(newValidations);
      setShouldValidateField(newShouldValidateField);
      setEverythingIsValid(allValidationsPassed);
    }
    if (field === "all" || !field) {
      return allValidationsPassed;
    }
    return newValidations[field];
  }

  function validateAndScrollToError(field?: keyof Y | "all") {
    const validated = validate(field);
    if (!validated) {
      setTimeout(scrollToNearestError, 100);
    }
    return validated;
  }

  function validateField(field: keyof Y) {
    setShouldValidateField({
      ...shouldValidateField,
      [field]: true,
    });
    validate(field);
  }

  return {
    isEverythingValid,
    clearValidation,
    validate,
    validateAndScrollToError,
    validateField,
    validations,
    errorMessages,
    validatingField: internalShouldValidateField,
  };
}

export function scrollToNearestError() {
  const errorFound =
    document.querySelector("[data-error='true']") ||
    document.querySelector("[data-name='caption'][color='var(--manifest-cherry9)']");
  if (!errorFound) return;
  const headerHeight = (document.querySelector("#header") as HTMLDivElement).getBoundingClientRect().height;
  let scrollToY = 0;
  if (errorFound) {
    const errorFoundDimensions = errorFound.getBoundingClientRect();
    scrollToY = errorFoundDimensions.top + window.pageYOffset - headerHeight - 15;

    // If we are almost at the top of the page, just scroll to top
    if (scrollToY < 200) {
      scrollToY = 0;
    }
    // eslint-disable-next-line no-alert
    window.scrollTo({
      top: scrollToY,
      behavior: "smooth",
    });
  }
}

/**
 * Represents a form component
 * @property props.submit - a function that is called when the form is submitted
 * @property props.error - an optional error message as a string or JSX Element
 */
const Form = (
  props: PropsWithChildren<
    Partial<{
      submit: (args?: any) => any;
      error?: string | JSX.Element;
      className?: string;
    }>
  >
) => {
  async function handleSave(event?: FormEvent<HTMLFormElement>) {
    if (event) {
      event.preventDefault();
    }

    if (props.submit) {
      let validated = false;
      try {
        validated = await props.submit();
      } catch (error) {
        // Do nothing for now
      }
      if (!validated) {
        setTimeout(() => {
          scrollToNearestError();
        }, 200);
      }
    }
  }

  return (
    <>
      {props.error && (
        <>
          <ErrorMessage>{props.error}</ErrorMessage>
          <Spacing $space={30} />
        </>
      )}
      <form noValidate onSubmit={handleSave} className={props.className}>
        {props.children}
      </form>
    </>
  );
};

export default Form;
